import { useRef, useState } from "react";
import axios from "axios";
import {
  Upload, FileJson, CheckCircle2, AlertCircle, Loader2, Clock,
  ClipboardPaste, Trash2, Calendar, Star, Copy, Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE = JSON.stringify(
  [
    {
      title: "Morning Mercies",
      content_html: "<p>Every morning, the steadfast love of the Lord meets us anew...</p>",
      excerpt: "His compassions never fail — they are new every morning.",
      status: "publish",
      published_at: "2026-06-21T11:00:00Z",
      categories: ["devotionals"],
      tags: ["faith", "monday"],
      is_featured: false,
    },
    {
      title: "Abiding in the Vine",
      content_html: "<p>“I am the vine, ye are the branches...”</p>",
      excerpt: "Fruitfulness flows from abiding.",
      status: "publish",
      published_at: "2026-06-22T11:00:00Z",
      categories: ["devotionals"],
      tags: ["faith", "abiding"],
      is_featured: true,
    },
  ],
  null,
  2
);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function BulkImport({ token, onImported }) {
  const fileRef = useRef(null);
  const [raw, setRaw] = useState("");
  const [parseError, setParseError] = useState("");
  const [validation, setValidation] = useState(null); // {count, sample}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {imported, failed, created[], errors[]}
  const [serverError, setServerError] = useState("");

  const tryParse = (text) => {
    setParseError("");
    setValidation(null);
    const t = (text || "").trim();
    if (!t) return null;
    let parsed;
    try {
      parsed = JSON.parse(t);
    } catch (e) {
      setParseError("Invalid JSON: " + e.message);
      return null;
    }
    // Accept either [ ... ] or { posts: [...] }
    let arr;
    if (Array.isArray(parsed)) arr = parsed;
    else if (parsed && Array.isArray(parsed.posts)) arr = parsed.posts;
    else {
      setParseError("Expected a JSON array of posts, or an object like { \"posts\": [ ... ] }.");
      return null;
    }
    if (arr.length === 0) {
      setParseError("Array is empty — add at least one post.");
      return null;
    }
    // Light per-item validation for preview only
    const issues = [];
    arr.forEach((p, i) => {
      if (!p || typeof p !== "object") issues.push(`#${i + 1}: not an object`);
      else {
        if (!p.title || typeof p.title !== "string" || !p.title.trim()) issues.push(`#${i + 1}: missing title`);
        if (p.published_at && isNaN(Date.parse(p.published_at))) issues.push(`#${i + 1}: invalid published_at (not ISO 8601)`);
        if (p.categories && !Array.isArray(p.categories)) issues.push(`#${i + 1}: categories must be an array`);
        if (p.tags && !Array.isArray(p.tags)) issues.push(`#${i + 1}: tags must be an array`);
      }
    });
    setValidation({ count: arr.length, issues, sample: arr.slice(0, 3) });
    return arr;
  };

  const onChangeRaw = (v) => {
    setRaw(v);
    setResult(null);
    setServerError("");
    tryParse(v);
  };

  const onFile = async (file) => {
    if (!file) return;
    if (!/\.json$/i.test(file.name) && file.type !== "application/json") {
      setParseError("Please upload a .json file.");
      return;
    }
    const text = await file.text();
    setRaw(text);
    setResult(null);
    setServerError("");
    tryParse(text);
  };

  const doImport = async () => {
    const arr = tryParse(raw);
    if (!arr) return;
    const hasBlockingIssue = (validation?.issues || []).some((m) => m.includes("missing title") || m.includes("not an object") || m.includes("invalid published_at"));
    if (hasBlockingIssue && !window.confirm("Some rows look invalid and will be skipped. Continue importing the valid ones?")) {
      return;
    }
    setSubmitting(true);
    setServerError("");
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/posts/bulk`,
        { posts: arr },
        { headers: { "X-Admin-Token": token }, timeout: 60000 }
      );
      setResult(r.data);
      onImported?.();
    } catch (e) {
      setServerError(e.response?.data?.detail
        ? (typeof e.response.data.detail === "string" ? e.response.data.detail : JSON.stringify(e.response.data.detail))
        : e.message || "Import failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const insertSample = () => onChangeRaw(SAMPLE);
  const clear = () => { setRaw(""); setParseError(""); setValidation(null); setResult(null); setServerError(""); };
  const copySample = async () => {
    try { await navigator.clipboard.writeText(SAMPLE); } catch { /* noop */ }
  };

  return (
    <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden" data-testid="bulk-import">
      <div className="p-5 border-b border-cream-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-navy-deep flex items-center gap-2">
            <FileJson size={18} className="text-gold" /> Bulk Import Devotionals
          </h3>
          <p className="text-sm text-navy/55 mt-1">
            Paste or upload a JSON array of posts. Future-dated posts auto-schedule themselves to publish at <code className="text-xs bg-cream-100 px-1.5 py-0.5 rounded">published_at</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          <button data-testid="bulk-upload-btn" onClick={() => fileRef.current?.click()}
            className="btn-outline-navy px-4 py-2 rounded-full text-sm inline-flex items-center gap-2">
            <Upload size={14} /> Upload .json
          </button>
          <button data-testid="bulk-sample-btn" onClick={insertSample}
            className="btn-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2">
            <ClipboardPaste size={14} /> Insert sample
          </button>
        </div>
      </div>

      <div className="p-5 grid lg:grid-cols-5 gap-5">
        {/* Left: paste box */}
        <div className="lg:col-span-3">
          <label className="block text-sm font-medium text-navy mb-1.5">JSON payload</label>
          <textarea
            data-testid="bulk-textarea"
            value={raw}
            onChange={(e) => onChangeRaw(e.target.value)}
            placeholder='Paste a JSON array of posts, e.g. [ { "title": "...", "content_html": "...", "published_at": "2026-06-21T11:00:00Z", ... } ]'
            rows={20}
            className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none font-mono text-xs leading-relaxed"
          />
          {parseError && (
            <p data-testid="bulk-parse-error" className="mt-2 text-sm text-red-700 inline-flex items-center gap-1.5">
              <AlertCircle size={14} /> {parseError}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button data-testid="bulk-import-btn" onClick={doImport}
              disabled={submitting || !raw.trim() || !!parseError || !validation}
              className="btn-primary px-6 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {submitting ? "Importing…" : `Import ${validation?.count ? `${validation.count} posts` : "posts"}`}
            </button>
            <button data-testid="bulk-clear-btn" onClick={clear} disabled={!raw} className="text-sm text-navy/60 hover:text-red-600 px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-40">
              <Trash2 size={14} /> Clear
            </button>
          </div>
          {serverError && (
            <div data-testid="bulk-server-error" className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm inline-flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /> {serverError}
            </div>
          )}
        </div>

        {/* Right: preview + format help */}
        <aside className="lg:col-span-2 space-y-4">
          {validation && (
            <div data-testid="bulk-preview" className="rounded-xl border border-gold/40 bg-gradient-to-br from-cream-50 to-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={16} className="text-green-700" />
                <span className="text-sm font-semibold text-navy-deep">
                  Detected {validation.count} post{validation.count === 1 ? "" : "s"}
                </span>
              </div>
              {validation.issues.length > 0 && (
                <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  <div className="font-semibold mb-1 inline-flex items-center gap-1"><AlertCircle size={12} /> {validation.issues.length} potential issue{validation.issues.length === 1 ? "" : "s"}:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {validation.issues.slice(0, 6).map((m) => <li key={m}>{m}</li>)}
                    {validation.issues.length > 6 && <li>…and {validation.issues.length - 6} more</li>}
                  </ul>
                </div>
              )}
              <div className="mt-3 text-xs text-navy/60 uppercase tracking-wider">Preview (first 3)</div>
              <ul className="mt-1 space-y-1.5 text-sm">
                {validation.sample.map((p, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Clock size={12} className="text-gold mt-1 shrink-0" />
                    <div>
                      <div className="font-medium text-navy-deep">{p.title || <em className="text-red-600">missing title</em>}</div>
                      {p.published_at && (
                        <div className="text-xs text-navy/55">{fmtDate(p.published_at)}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-xl border border-cream-200 bg-cream-50 p-4 text-xs text-navy/75 leading-relaxed">
            <div className="font-semibold text-navy-deep mb-1.5 inline-flex items-center gap-1.5">
              <FileJson size={14} className="text-gold" /> Format
            </div>
            <ul className="space-y-1 list-disc list-inside">
              <li><code>title</code> — required</li>
              <li><code>content_html</code> — HTML body</li>
              <li><code>excerpt</code> — card teaser</li>
              <li><code>status</code> — "publish" (auto → "future" if dated ahead)</li>
              <li><code>published_at</code> — ISO 8601, e.g. <code>2026-06-21T11:00:00Z</code> (11:00 UTC = 6:00 AM Central)</li>
              <li><code>categories</code> — array of slugs, e.g. <code>["devotionals"]</code></li>
              <li><code>tags</code> — array of slugs</li>
              <li><code>is_featured</code> — boolean</li>
            </ul>
            <button onClick={copySample} className="mt-3 text-xs inline-flex items-center gap-1 text-navy-deep hover:text-gold underline">
              <Copy size={11} /> Copy sample JSON
            </button>
          </div>
        </aside>
      </div>

      {/* Result */}
      {result && (
        <div className="border-t border-cream-200 p-5" data-testid="bulk-result">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 text-green-800 text-sm font-semibold">
              <CheckCircle2 size={16} /> {result.imported} imported
            </span>
            {result.failed > 0 && (
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 text-red-800 text-sm font-semibold">
                <AlertCircle size={16} /> {result.failed} failed
              </span>
            )}
          </div>

          {result.created?.length > 0 && (
            <div className="rounded-xl border border-cream-200 overflow-hidden mb-4">
              <div className="px-4 py-2 bg-cream-50 border-b border-cream-200 text-xs uppercase tracking-wider text-navy/60 font-semibold">
                Scheduled posts
              </div>
              <ul className="divide-y divide-cream-100">
                {result.created.map((c) => (
                  <li key={c.id} data-testid={`bulk-created-${c.slug}`} className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium text-navy-deep flex items-center gap-2 flex-wrap">
                        {c.title}
                        {c.is_featured && (
                          <span className="inline-flex items-center gap-1 text-gold text-xs font-semibold">
                            <Star size={11} fill="currentColor" /> Featured
                          </span>
                        )}
                      </div>
                      <code className="text-[0.7rem] text-navy/55">{c.slug}</code>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--gold-600)]">
                        <Calendar size={13} /> {fmtDate(c.published_at)}
                      </div>
                      <StatusPill status={c.status} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.errors?.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 overflow-hidden">
              <div className="px-4 py-2 bg-red-100 border-b border-red-200 text-xs uppercase tracking-wider text-red-900 font-semibold">
                Errors
              </div>
              <ul className="divide-y divide-red-100">
                {result.errors.map((e, i) => (
                  <li key={i} className="px-4 py-3 text-sm">
                    <div className="font-medium text-red-900">Row {e.index + 1}{e.title ? `: ${e.title}` : ""}</div>
                    <div className="text-xs text-red-800 mt-0.5 font-mono whitespace-pre-wrap">{e.error}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <button onClick={clear} className="btn-outline-navy px-5 py-2 rounded-full text-sm inline-flex items-center gap-2">
              Import more
            </button>
            <button
              onClick={() => {
                const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `bulk-import-${new Date().toISOString().slice(0, 10)}.json`;
                a.click();
              }}
              className="text-sm text-navy/60 hover:text-navy-deep px-3 py-2 inline-flex items-center gap-1.5">
              <Download size={14} /> Download report
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function StatusPill({ status }) {
  const map = {
    publish: { bg: "bg-green-100", color: "text-green-800", label: "Published" },
    future: { bg: "bg-gold/15", color: "text-[var(--gold-600)]", label: "Scheduled" },
    draft: { bg: "bg-cream-100", color: "text-navy/70", label: "Draft" },
  };
  const m = map[status] || { bg: "bg-cream-100", color: "text-navy/70", label: status };
  return <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-[0.65rem] uppercase font-semibold tracking-wider ${m.bg} ${m.color}`}>{m.label}</span>;
}
